import type { StatusTone } from '~/client/components/base/status-pill';
import * as GQL from '~/graphql/generated/client';

export interface PublishStatusDisplay {
  label: string;
  tone: StatusTone;
}

const ATTEMPT_DISPLAY: Record<
  GQL.GuardianPublicationStatus,
  PublishStatusDisplay
> = {
  [GQL.GuardianPublicationStatus.Published]: {
    label: 'Published to Witness',
    tone: 'success',
  },
  [GQL.GuardianPublicationStatus.Unchanged]: {
    label: 'Published to Witness',
    tone: 'success',
  },
  [GQL.GuardianPublicationStatus.NothingToPublish]: {
    label: 'Nothing to publish',
    tone: 'neutral',
  },
  [GQL.GuardianPublicationStatus.Refused]: {
    label: 'Publish refused',
    tone: 'warning',
  },
  [GQL.GuardianPublicationStatus.Failed]: {
    label: 'Publish failed',
    tone: 'danger',
  },
};

// unpublishedChanges is null or undefined when the server cannot tell or has not answered yet.
export function publishStatusDisplay(
  lastStatus: GQL.GuardianPublicationStatus | undefined,
  unpublishedChanges: boolean | null | undefined
): PublishStatusDisplay {
  if (!lastStatus) {
    return { label: 'Not yet published to Witness', tone: 'neutral' };
  }
  const attemptFailed =
    lastStatus === GQL.GuardianPublicationStatus.Refused ||
    lastStatus === GQL.GuardianPublicationStatus.Failed;
  if (unpublishedChanges === true && !attemptFailed) {
    return { label: 'Changes not yet published', tone: 'warning' };
  }
  const published =
    lastStatus === GQL.GuardianPublicationStatus.Published ||
    lastStatus === GQL.GuardianPublicationStatus.Unchanged;
  if (published && unpublishedChanges !== false) {
    return { label: 'Last published to Witness', tone: 'neutral' };
  }
  return ATTEMPT_DISPLAY[lastStatus];
}

export interface PublishOutcomeMessage {
  text: string;
  isProblem: boolean;
}

export function publishOutcomeMessage(
  publication: Pick<
    GQL.GuardianPublicationFieldsFragment,
    | 'status'
    | 'stage'
    | 'reason'
    | 'httpStatus'
    | 'bundleVersion'
    | 'controlCount'
  >
): PublishOutcomeMessage {
  const version = publication.bundleVersion
    ? ` (version ${publication.bundleVersion})`
    : '';
  const why = publication.reason ? `: ${publication.reason}` : '';
  switch (publication.status) {
    case GQL.GuardianPublicationStatus.Published:
      return {
        text: `Published to Witness${version} · ${publication.controlCount} controls`,
        isProblem: false,
      };
    case GQL.GuardianPublicationStatus.Unchanged:
      return {
        text: `Nothing new to send${version}: the policy matches the last publication`,
        isProblem: false,
      };
    case GQL.GuardianPublicationStatus.NothingToPublish:
      return {
        text: `Nothing to publish${why || ': no runtime policy applies here'}`,
        isProblem: false,
      };
    case GQL.GuardianPublicationStatus.Refused:
      return { text: `Publish refused${why}`, isProblem: true };
    default: {
      const stage = publication.stage
        ? ` at ${publication.stage.toLowerCase()}`
        : '';
      const http = publication.httpStatus
        ? ` (HTTP ${publication.httpStatus})`
        : '';
      return { text: `Publish failed${stage}${http}${why}`, isProblem: true };
    }
  }
}
